/**
 * The `(setup)` group layout (ADR 0006).
 *
 * `src/app/(setup)/_layout.tsx` is a two-line re-export of this component. The
 * implementation lives outside `src/app` because expo-router's `require.context`
 * turns every file under the app directory into a route -- test files included --
 * so a co-located `__tests__` folder there would be published as routes.
 *
 * Three responsibilities, all of them consequences of setup being a route group
 * rather than a stack of overlays:
 *
 *  1. Mount `SetupProvider`, so the flow's answers survive navigation between
 *     step screens and die with the group.
 *  2. Enforce step order. A route ahead of the server's `current_step` redirects
 *     back to where the user actually is; at or behind is allowed, because that
 *     is just Back.
 *  3. Suppress escape from the entry screen. `gestureEnabled: false` closes the
 *     iOS swipe, and an Android hardware back handler consumes the press on the
 *     one screen that has nothing behind it.
 */

import { Redirect, Stack, useSegments } from 'expo-router';
import * as React from 'react';
import { BackHandler } from 'react-native';

import {
  resolveSetupRoute,
  type SetupRouteName,
} from '@/features/setup/gate/resolve-setup-route';
import { SetupProvider, useSetupContext } from '@/features/setup/setup-provider';
import { useAuth } from '@/hooks/use-auth';

import { SETUP_ROUTE_HREFS } from './setup-hrefs';

const SETUP_ROUTE_NAMES: readonly SetupRouteName[] = [
  'username',
  'welcome',
  'permissions',
  'intent',
  'taste',
  'friends',
  'done',
];

/** `null` for the group's `index` dispatcher, which resolves its own target. */
function requestedRouteFromSegments(segments: readonly string[]): SetupRouteName | null {
  const last = segments[segments.length - 1];
  return SETUP_ROUTE_NAMES.includes(last as SetupRouteName) ? (last as SetupRouteName) : null;
}

/**
 * Consumes the Android hardware back press on the screen with nothing behind it.
 * Every other setup screen leaves it alone: Back there is a real pop, and the
 * header's Back affordance is the same move.
 */
function useSuppressEntryBack(isEntryScreen: boolean): void {
  React.useEffect(() => {
    if (!isEntryScreen) return;

    const subscription = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => subscription.remove();
  }, [isEntryScreen]);
}

function SetupStepGuard() {
  const { user } = useAuth();
  const { currentStep } = useSetupContext();
  const segments = useSegments();

  const usernamePending = user?.usernamePending === true;
  const requestedRoute = requestedRouteFromSegments(segments as readonly string[]);
  const entryRoute: SetupRouteName = usernamePending ? 'username' : 'welcome';

  useSuppressEntryBack(requestedRoute === entryRoute);

  // The dispatcher resolves its own destination; guarding it too would race it.
  if (requestedRoute === null) return null;

  // The claim rides the auth session and is known immediately. Onboarding's step
  // is not, and bouncing off a placeholder would fight the resume it exists for.
  if (!usernamePending && currentStep === null) return null;

  const redirectTo = resolveSetupRoute({ usernamePending, currentStep, requestedRoute });
  if (redirectTo === null) return null;

  return <Redirect href={SETUP_ROUTE_HREFS[redirectTo]} />;
}

export function SetupGroupLayout() {
  return (
    <SetupProvider>
      <SetupStepGuard />
      {/* Titles are the browser/page chrome, matching the (auth) layout. */}
      <Stack screenOptions={{ headerShown: false, gestureEnabled: false }}>
        <Stack.Screen name="index" />
        <Stack.Screen name="username" options={{ title: 'Pick a Username' }} />
        <Stack.Screen name="welcome" options={{ title: 'Welcome' }} />
        <Stack.Screen name="permissions" options={{ title: 'Permissions' }} />
        <Stack.Screen name="intent" options={{ title: 'What Brings You Here' }} />
        <Stack.Screen name="taste" options={{ title: 'Your Tastes' }} />
        <Stack.Screen name="friends" options={{ title: 'Find Friends' }} />
        <Stack.Screen name="done" options={{ title: 'All Set' }} />
      </Stack>
    </SetupProvider>
  );
}
